import CreditCardIcon from '@atlaskit/icon/core/credit-card';
import TeamsIcon from '@atlaskit/icon/core/teams';
import OldBillingIcon from '@atlaskit/icon/glyph/billing';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import OldTeamsIcon from '@atlaskit/icon/glyph/teams';
import { LinkItem, Section, LinkItemProps } from '@atlaskit/menu';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';

import { useAnalytics } from '@townsquare/analytics';
import { getConfig } from '@townsquare/config';
import { useIsNavRefreshEnabled } from '@townsquare/stat-sig/nav4';
import { useAdminHubLinkForSite, useBillingPreviewUrl } from '@townsquare/url-utils';
import { useProductDisplayName } from '@townsquare/workspace-store';

import { AdminLinksQuery } from './__generated__/AdminLinksQuery.graphql';

const { macUrl } = getConfig();

type SettingsAdminLinksProps = {
  queryRef: PreloadedQuery<AdminLinksQuery>;
};

export const SettingsAdminLinks = (props: SettingsAdminLinksProps) => {
  const adminData = usePreloadedQuery<AdminLinksQuery>(
    graphql`
      query AdminLinksQuery($id: ID!) {
        workspace(id: $id) {
          userWorkspacePermissions {
            admin
          }
        }
      }
    `,
    props.queryRef,
  );

  if (!adminData.workspace?.userWorkspacePermissions?.admin) {
    return null;
  }

  return <AdminLinks />;
};

export function AdminLinks() {
  const intl = useIntl();
  const analytics = useAnalytics();

  const adminHubUrlForSite = useAdminHubLinkForSite();
  const billingPreviewUrl = useBillingPreviewUrl();
  const productDisplayName = useProductDisplayName();
  const isNewNavExperience = useIsNavRefreshEnabled();

  const handleCloudAdminSettingsClick: LinkItemProps['onClick'] = e => {
    e.preventDefault();
    void analytics.ui('cloudAdministrationSettings', 'clicked');
    open(adminHubUrlForSite, '_blank', 'noreferrer');
  };

  const handleLicensingSettingsClick: LinkItemProps['onClick'] = e => {
    e.preventDefault();
    void analytics.ui('licensingSettings', 'clicked');
    open(macUrl, '_blank', 'noreferrer');
  };

  const handleBillingSettingsClick: LinkItemProps['onClick'] = e => {
    e.preventDefault();
    void analytics.ui('billingSettings', 'clicked');
    open(billingPreviewUrl, '_blank', 'noreferrer');
  };

  return (
    <Section
      title={
        isNewNavExperience
          ? intl.formatMessage(
              {
                id: 'townsquare.navigation.settings.admin-links-heading',
                description: 'Heading for a admin links inside the settings dropdown',
                defaultMessage: '{productDisplayName} admin settings',
              },
              {
                productDisplayName: productDisplayName,
              },
            )
          : undefined
      }
    >
      <LinkItem
        target="_blank"
        onClick={handleCloudAdminSettingsClick}
        iconAfter={<ShortcutIcon label="" size="small" />}
        href={'' + adminHubUrlForSite}
        description={
          isNewNavExperience
            ? intl.formatMessage({
                id: 'townsquare.navigation.settings.user-management-settings-description',
                defaultMessage: 'Manage users, groups, and access requests',
                description: 'Description for the user management settings link in the settings dropdown',
              })
            : intl.formatMessage({
                id: 'townsquare.navigation.settings.cloud-administration-settings-description',
                defaultMessage: 'Manage users and security',
                description: 'Description for the cloud administration settings link in the settings dropdown',
              })
        }
        testId="nav-cloud-admin-settings-link"
        iconBefore={isNewNavExperience && <TeamsIcon label="" LEGACY_fallbackIcon={OldTeamsIcon} />}
        shouldDescriptionWrap
      >
        {isNewNavExperience ? (
          <FormattedMessage
            id="townsquare.navigation.settings.user-management-settings"
            defaultMessage="User management"
            description="Label for the user management settings link in the settings dropdown"
          />
        ) : (
          <FormattedMessage
            id="townsquare.navigation.settings.cloud-administration-settings"
            defaultMessage="Cloud Administration"
            description="Label for the cloud administration settings link in the settings dropdown"
          />
        )}
      </LinkItem>
      <LinkItem
        href={'' + macUrl}
        onClick={handleLicensingSettingsClick}
        iconAfter={<ShortcutIcon label="" size="small" />}
        description={intl.formatMessage({
          id: 'townsquare.navigation.settings.licensing-settings-description',
          defaultMessage: 'Server and Data Center licensing',
          description: 'Description for the licensing settings link in the settings dropdown',
        })}
        iconBefore={isNewNavExperience && <CreditCardIcon label="" LEGACY_fallbackIcon={OldBillingIcon} />}
        testId="nav-licensing-settings-link"
        shouldDescriptionWrap
      >
        <FormattedMessage
          id="townsquare.navigation.settings.licensing-settings"
          defaultMessage="Licensing"
          description="Label for the licensing settings link in the settings dropdown"
        />
      </LinkItem>
      <LinkItem
        href={'' + billingPreviewUrl}
        onClick={handleBillingSettingsClick}
        iconAfter={<ShortcutIcon label="" size="small" />}
        description={
          isNewNavExperience
            ? intl.formatMessage({
                id: 'townsquare.navigation.settings.billing-settings-explanation',
                defaultMessage: 'Update your billing details, manage subscriptions, and more',
                description: 'Explanation for the billing settings link in the settings dropdown',
              })
            : intl.formatMessage({
                id: 'townsquare.navigation.settings.billing-settings-description',
                defaultMessage: 'Manage billing',
                description: 'Description for the billing settings link in the settings dropdown',
              })
        }
        iconBefore={isNewNavExperience && <CreditCardIcon label="" LEGACY_fallbackIcon={OldBillingIcon} />}
        testId="nav-billing-preview-link"
        shouldDescriptionWrap
      >
        <FormattedMessage
          id="townsquare.navigation.settings.billing-settings"
          defaultMessage="Billing"
          description="Label for the billing settings link in the settings dropdown"
        />
      </LinkItem>
    </Section>
  );
}
